import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid } from 'semantic-ui-react';

import { FieldLabel } from '../../../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../../../services/properties';
import { maskZipCode } from '../../../../../services/masks';
import { StepProps } from '../../../BondInsuranceForm';
import { mapWalletPropertyTypeToHireType } from '../../../utils/mapWalletPropertyTypeToHireType';

type PropertyWalletSearchDropdownProps = Pick<StepProps, 'setFieldValue' | 'values'> & {
  estateId?: string;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

const PropertyWalletSearchDropdown = ({
  estateId,
  setFieldValue,
  values
}: PropertyWalletSearchDropdownProps): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  const selectedPublicId = values?.property_info?.property_public_id;

  useEffect(() => {
    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId: estateId || undefined,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => ({
    key: item.publicId,
    value: item.publicId,
    text: item.identification || 'Sem código do imóvel',
    description: getAddressLabel(item)
  }));

  return (
    <Grid.Row>
      <Grid.Column mobile={16} tablet={16} computer={16}>
        <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
        <Dropdown
          fluid
          selection
          clearable
          search
          loading={isLoading}
          options={options}
          value={selectedPublicId || ''}
          noResultsMessage="Nenhum imóvel encontrado."
          placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
          onSearchChange={(_event, data: DropdownProps) => {
            setSearch(String(data.searchQuery || ''));
          }}
          onChange={(_event, data: DropdownProps) => {
            const selectedId = String(data.value || '');

            if (!selectedId) {
              setFieldValue('property_info.property_public_id', undefined);
              setFieldValue('property_info.property_code', '');
              return;
            }

            const selected = itemsByPublicId.get(selectedId);
            if (!selected) return;

            const mappedPropertyType = mapWalletPropertyTypeToHireType(
              selected,
              values?.property_info?.property_type
            );

            setFieldValue('property_info.property_public_id', selected.publicId);
            setFieldValue('property_info.property_code', selected.identification || '');
            setFieldValue(
              'property_info.zip_code',
              maskZipCode(selected?.address?.zipcode || '')
            );
            setFieldValue('property_info.street', selected?.address?.street || '');
            setFieldValue('property_info.district', selected?.address?.neighborhood || '');
            setFieldValue('property_info.city', selected?.address?.city || '');
            setFieldValue('property_info.state', selected?.address?.state || '');
            setFieldValue('property_info.number', selected?.address?.number || '');
            setFieldValue('property_info.complement', selected?.address?.complement || '');
            setFieldValue('property_info.property_type', mappedPropertyType || '');
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

export default PropertyWalletSearchDropdown;
